//@flow

import React, { useEffect, useState } from "react";

import { classNameOf } from "@/utils";

export type BruImageProps = {|
    image: ImageSharp,
    alt: string,
    /**
     * Load image immediately or lazy (Defaults to "lazy").
     * Avoid setting "lazy" on images that are in the first visible viewport:
     * https://web.dev/native-lazy-loading/#avoid-lazy-loading-images-that-are-in-the-first-visible-viewport
     */
    loading?: "eager" | "lazy",
    className?: string,
    style?: $Shape<CSSStyleDeclaration>,
    forwardRef?: { current: HTMLImageElement | null },
|};

type Size = {
    unit: "px" | "%",
    value: number,
    type: "width" | "height",
};

type SrcSetEntry = {
    size: Size,
    url: string,
};

function parseSize(size: string): Size {
    const unit = "px";
    const pattern = /^(\d+)([h|w])$/;
    const match = size.match(pattern);
    if (match != null && match.length > 2) {
        const type = match[2] === "w" ? "width" : "height";
        return { unit, value: parseInt(match[1]), type };
    }
    return { unit, type: "width", value: 0 };
}

function parseSrcSetString(srcSet: string): SrcSetEntry[] {
    return srcSet
        .split(",") // Split entries by ","
        .map(line => line.replace(/(\r\n|\n|\r)/gm, "").split(" ")) // Split the entries by " " (url, size)
        .filter(entry => entry.length >= 2) // Filter entries that don't have 2 items
        .map(([url, size]) => ({ url, size: parseSize(size) })) // Convert to SrcSetEntry
        .sort((a, b) => a.size.value - b.size.value); // Sort by lowest value first
}

type SourceProps = {
    size: Size,
    url: string,
    min: boolean,
    type?: string,
    useDataSrcSet: boolean
}

const Source = ({
    size,
    url,
    min,
    type,
    useDataSrcSet,
}: SourceProps): React$Element<"source"> => {
    const attribute = min ? `min-${size.type}` : `max-${size.type}`;
    const sizeWithUnit = `${size.value}${size.unit}`;
    const srcSetProp = useDataSrcSet ? { "data-srcset": url } : { srcSet: url };
    return (
        <source
            type={type}
            media={`(${attribute}: ${sizeWithUnit})`}
            {...srcSetProp}
        />
    );
};

const Sources = ({
    srcSet,
    type,
    useDataSrcSet,
}: {
    srcSet?: string,
    type?: string,
    useDataSrcSet: boolean,
}) => {
    const entries = parseSrcSetString(srcSet || "");

    return entries.map((entry, index) => (
        <Source
            key={index}
            type={type}
            size={entry.size}
            url={entry.url}
            min={false}
            useDataSrcSet={useDataSrcSet}
        />
    ));
};

const BruImage = ({image, alt, loading = "lazy", style, className, forwardRef}: BruImageProps): React$Element<"picture"> => {
    const { original, fluid } = image;
    /**
     * When loading is "lazy" use `data-src` initially, when component mounts
     * we can check if the browser support lazy loading and change it to `src`
     *
     * If loading is not "lazy", always use `src`
     */
    const [useDataSrc, setUseDataSrc] = useState(loading === "lazy");

    useEffect(() => {
        // Check for lazy loading support in loading is "lazy"
        if (loading === "lazy") {
            if ("loading" in HTMLImageElement.prototype) {
                setUseDataSrc(false);
            } else {
                setUseDataSrc(true);
            }
        }
    }, [loading, image, setUseDataSrc]);

    const srcProp = useDataSrc
        ? { "data-src": image.original.src }
        : { src: image.original.src };

    return (
        <picture>
            {/* Add .webp sources first (If they exist) */}
            <Sources
                type="image/webp"
                srcSet={fluid?.srcSetWebp}
                useDataSrcSet={useDataSrc}
            />

            {/* Add .jpg/.png sources (If they exist) */}
            <Sources srcSet={fluid?.srcSet} useDataSrcSet={useDataSrc} />

            {/* Fallback image (If no sourceSet was specified, or is not supported by the browser) */}
            <img
                ref={forwardRef}
                alt={alt}
                data-aspect-ratio={fluid?.aspectRatio}
                loading={loading}
                width={original.width}
                height={original.height}
                style={style}
                className={classNameOf(className || "", {
                    lazyload: loading === "lazy",
                })}
                {...srcProp}
            />
        </picture>
    );
};

export default BruImage;
